import { z } from "zod";

import { MetaDataSchema } from "@/types/meta-data";

// ── JSON wrapper for array fields ──

export const JsonItemsWrapperSchema = z.object({
	items: z.array(z.unknown()),
});

// ── ICPs ──

export const KnowledgeDbIcpSchema = z.object({
	id: z.number(),
	profile: z.string().nullable().optional(),
	painPoints: z.record(z.string(), z.unknown()).nullable().optional(),
	currentSolutions: z.string().nullable().optional(),
	marketTrends: z.string().nullable().optional(),
	obstacles: z.record(z.string(), z.unknown()).nullable().optional(),
	opportunities: z.record(z.string(), z.unknown()).nullable().optional(),
	featuresBenefits: z.record(z.string(), z.unknown()).nullable().optional(),
	contentStatuses: z.record(z.string(), z.string()).nullable().optional(),
	scorePersonaCategoryId: z.number(),
	scoreCompanyCategoryId: z.number().nullable().optional(),
	companyId: z.number(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export const KnowledgeDbIcpResponseSchema = z.object({
	data: z.array(KnowledgeDbIcpSchema),
	meta: MetaDataSchema,
});

export type KnowledgeDbIcpApi = z.infer<typeof KnowledgeDbIcpSchema>;
export type KnowledgeDbIcpResponseApi = z.infer<typeof KnowledgeDbIcpResponseSchema>;

// ── Products ──

export const TargetSegmentCategorySchema = z.object({
	id: z.number(),
	category: z.string(),
	description: z.string().nullable().optional(),
	type: z.string().nullable().optional(),
});

export const TargetSegmentSchema = z.object({
	id: z.number(),
	scoreCompanyCategoryId: z.number(),
	isActive: z.boolean(),
	scoreCompanyCategory: TargetSegmentCategorySchema.nullable().optional(),
});

export const KnowledgeDbProductSchema = z.object({
	id: z.number(),
	name: z.string().nullable().optional(),
	type: z.string().nullable().optional(),
	description: z.string().nullable().optional(),
	keyFeatures: z.record(z.string(), z.unknown()).nullable().optional(),
	competitiveDiff: z.string().nullable().optional(),
	pricing: z.string().nullable().optional(),
	status: z.string().nullable().optional(),
	targetSegments: z.array(TargetSegmentSchema).optional().default([]),
	companyId: z.number(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export const KnowledgeDbProductResponseSchema = z.object({
	data: z.array(KnowledgeDbProductSchema),
	meta: MetaDataSchema,
});

export type KnowledgeDbProductApi = z.infer<typeof KnowledgeDbProductSchema>;
export type KnowledgeDbProductResponseApi = z.infer<typeof KnowledgeDbProductResponseSchema>;

// ── Case Studies ──

export const KnowledgeDbCaseStudySchema = z.object({
	id: z.number(),
	clientName: z.string().nullable().optional(),
	website: z.string().nullable().optional(),
	industry: z.array(z.string()).nullable().optional(),
	employeeCount: z.number().nullable().optional(),
	description: z.string().nullable().optional(),
	enrichmentSource: z.string().nullable().optional(),
	matchedSegment: z.string().nullable().optional(),
	companyId: z.number(),
	projectName: z.string().nullable().optional(),
	goalsChallenge: z.string().nullable().optional(),
	solution: z.string().nullable().optional(),
	outcome: z.string().nullable().optional(),
	quote: z.string().nullable().optional(),
	quoteAttribution: z.string().nullable().optional(),
	sourceUrl: z.string().nullable().optional(),
	detectedDate: z.string().nullable().optional(),
	status: z.string().nullable().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export const KnowledgeDbCaseStudyResponseSchema = z.object({
	data: z.array(KnowledgeDbCaseStudySchema),
	meta: MetaDataSchema,
});

export type KnowledgeDbCaseStudyApi = z.infer<typeof KnowledgeDbCaseStudySchema>;
export type KnowledgeDbCaseStudyResponseApi = z.infer<typeof KnowledgeDbCaseStudyResponseSchema>;

// ── Objections ──

export const KnowledgeDbObjectionSchema = z.object({
	id: z.number(),
	quote: z.string().nullable().optional(),
	underlyingConcern: z.string().nullable().optional(),
	strategies: z.record(z.string(), z.unknown()).nullable().optional(),
	status: z.string().nullable().optional(),
	categoryId: z.number().nullable().optional(),
	companyId: z.number(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export const KnowledgeDbObjectionResponseSchema = z.object({
	data: z.array(KnowledgeDbObjectionSchema),
	meta: MetaDataSchema,
});

export type KnowledgeDbObjectionApi = z.infer<typeof KnowledgeDbObjectionSchema>;
export type KnowledgeDbObjectionResponseApi = z.infer<typeof KnowledgeDbObjectionResponseSchema>;

// ── Objection Categories ──

export const KnowledgeDbObjectionCategorySchema = z.object({
	id: z.number(),
	name: z.string(),
	isCustom: z.boolean(),
	companyId: z.number(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export const KnowledgeDbObjectionCategoryResponseSchema = z.object({
	data: z.array(KnowledgeDbObjectionCategorySchema),
	meta: MetaDataSchema,
});

export type KnowledgeDbObjectionCategoryApi = z.infer<typeof KnowledgeDbObjectionCategorySchema>;
export type KnowledgeDbObjectionCategoryResponseApi = z.infer<
	typeof KnowledgeDbObjectionCategoryResponseSchema
>;

// ── Settings ──

export const KnowledgeDbSettingSchema = z.object({
	id: z.number(),
	userId: z.number(),
	autoUpdateEnabled: z.boolean(),
	caseStudyUrl: z.string().nullable().optional(),
	caseStudyDocumentUrl: z.string().nullable().optional(),
	lastAutoUpdate: z.string().nullable().optional(),
	autoUpdateInProgress: z.boolean(),
	companyId: z.number(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export type KnowledgeDbSettingApi = z.infer<typeof KnowledgeDbSettingSchema>;

// ── Delete Response ──

export const KnowledgeDbDeleteResponseSchema = z.object({
	id: z.number(),
});

export type KnowledgeDbDeleteResponse = z.infer<typeof KnowledgeDbDeleteResponseSchema>;

// ── Case Study Auto-Update Response ──

export const KnowledgeDbCaseStudyAutoUpdateResponseSchema = z.object({
	message: z.string(),
});

export type KnowledgeDbCaseStudyAutoUpdateResponse = z.infer<
	typeof KnowledgeDbCaseStudyAutoUpdateResponseSchema
>;

// ── Bulk Approve Response ──

export const KnowledgeDbBulkApproveResponseSchema = z.object({
	data: z.array(KnowledgeDbIcpSchema),
});

export type KnowledgeDbBulkApproveResponse = z.infer<typeof KnowledgeDbBulkApproveResponseSchema>;

export const KnowledgeDbBulkApproveProductsResponseSchema = z.object({
	data: z.array(KnowledgeDbProductSchema),
});

export type KnowledgeDbBulkApproveProductsResponse = z.infer<
	typeof KnowledgeDbBulkApproveProductsResponseSchema
>;

export const KnowledgeDbBulkApproveCaseStudiesResponseSchema = z.object({
	data: z.array(KnowledgeDbCaseStudySchema),
});

export type KnowledgeDbBulkApproveCaseStudiesResponse = z.infer<
	typeof KnowledgeDbBulkApproveCaseStudiesResponseSchema
>;

export const KnowledgeDbBulkApproveObjectionsResponseSchema = z.object({
	data: z.array(KnowledgeDbObjectionSchema),
});

export type KnowledgeDbBulkApproveObjectionsResponse = z.infer<
	typeof KnowledgeDbBulkApproveObjectionsResponseSchema
>;

// ── Segment Sync (Lead Scoring → Knowledge DB) ──

export const SegmentChangeItemSchema = z.object({
	changeType: z.enum(["NEW", "REMOVED", "CHANGED"]),
	scorePersonaCategoryId: z.number().optional(),
	personaCategory: z.string().optional(),
	scoreCompanyCategoryId: z.number().optional(),
	companyCategory: z.string().optional(),
	affectedIcpIds: z.array(z.number()).optional(),
	changedField: z.string().optional(),
	oldValue: z.string().optional(),
	newValue: z.string().optional(),
});

export type SegmentChangeItem = z.infer<typeof SegmentChangeItemSchema>;

export const KnowledgeDbChangesSchema = z.object({
	hasChanges: z.boolean(),
	changes: z.array(SegmentChangeItemSchema).optional().default([]),
	newSegmentCount: z.number().optional().default(0),
	removedSegmentCount: z.number().optional().default(0),
	changedSegmentCount: z.number().optional().default(0),
});

export type KnowledgeDbChanges = z.infer<typeof KnowledgeDbChangesSchema>;

export const PendingSegmentSyncSchema = z.object({
	id: z.number(),
	companyId: z.number(),
	changeSummary: z.object({
		changes: z.array(SegmentChangeItemSchema),
		newSegmentCount: z.number().optional().default(0),
		removedSegmentCount: z.number().optional().default(0),
		changedSegmentCount: z.number().optional().default(0),
	}),
	status: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export type PendingSegmentSync = z.infer<typeof PendingSegmentSyncSchema>;

export const PendingSegmentSyncListSchema = z.array(PendingSegmentSyncSchema);

export const ApplySegmentChangesResponseSchema = z.object({
	message: z.string(),
});

export type ApplySegmentChangesResponse = z.infer<typeof ApplySegmentChangesResponseSchema>;

export const DeletePendingSegmentSyncResponseSchema = z.object({
	message: z.string(),
});

export type DeletePendingSegmentSyncResponse = z.infer<
	typeof DeletePendingSegmentSyncResponseSchema
>;
